import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useDidUpdate, useStateInRef } from 'seagull';
import { useEventReporter, useStore, useStoreActions, useIoContext, useStoreStatic } from '~/contexts';
import { selectors } from '~/store/state-selectors';
function makeUserProperties(userInfo) {
    const userId = userInfo === null || userInfo === void 0 ? void 0 : userInfo.id();
    const { type, hasLinkedBankAccount, invitedUserCount, membersCount, ownership, kycCompletionStatus, businessTypes, csm, postalAddress, numSaleAgreements, numPurchaseAgreements, numSaleInvoices, numPurchaseInvoices, numSampleAgreements, role } = userInfo.business() || {};
    const firstName = userInfo.firstName();
    const lastName = userInfo.lastName();
    const phone = userInfo.phone();
    const referredBy = userInfo.referredByBusinessId();
    const numClientsAMonth = userInfo.numClientsAMonth();
    return _object_spread({
        id: userId,
        type: type,
        firstName,
        lastName,
        phone,
        numClientsAMonth,
        hasLinkedBankAccount,
        invitedUserCount,
        membersCount,
        ownership,
        kycCompletionStatus,
        numSaleAgreementsApproved: numSaleAgreements === null || numSaleAgreements === void 0 ? void 0 : numSaleAgreements.approved,
        numSaleAgreementsPending: numSaleAgreements === null || numSaleAgreements === void 0 ? void 0 : numSaleAgreements.pending,
        numPurchaseAgreementsApproved: numPurchaseAgreements === null || numPurchaseAgreements === void 0 ? void 0 : numPurchaseAgreements.approved,
        numPurchaseAgreementsPending: numPurchaseAgreements === null || numPurchaseAgreements === void 0 ? void 0 : numPurchaseAgreements.pending,
        numSaleInvoicesPaid: numSaleInvoices === null || numSaleInvoices === void 0 ? void 0 : numSaleInvoices.paid,
        numSaleInvoicesPending: numSaleInvoices === null || numSaleInvoices === void 0 ? void 0 : numSaleInvoices.pending,
        numPurchaseInvoicesPaid: numPurchaseInvoices === null || numPurchaseInvoices === void 0 ? void 0 : numPurchaseInvoices.paid,
        numPurchaseInvoicesPending: numPurchaseInvoices === null || numPurchaseInvoices === void 0 ? void 0 : numPurchaseInvoices.pending,
        numSampleAgreementsApproved: numSampleAgreements === null || numSampleAgreements === void 0 ? void 0 : numSampleAgreements.approved,
        numSampleAgreementsPending: numSampleAgreements === null || numSampleAgreements === void 0 ? void 0 : numSampleAgreements.pending,
        businessTypes,
        referredBy,
        csm,
        role,
        businessId: userInfo.businessId()
    }, postalAddress);
}
export default function AuthProvider(props) {
    const getProps = useStateInRef(props);
    const authToken = useStore((state)=>state.authToken);
    const userInfo = useStore((state)=>state.userInfo);
    const shouldLoadAppcues = useStore(selectors.shouldLoadAppcues);
    const [appcuesLoaded, setAppcuesLoaded] = useState(false);
    const { getState } = useStoreStatic();
    const isRealUser = useStore(selectors.isRealUser) && !!userInfo;
    const { reset: resetStore } = useStoreActions();
    const { getHotjar, cookies } = useIoContext();
    const bi = useEventReporter();
    const getBI = useStateInRef(bi);
    useDidUpdate(()=>{
        if (authToken == null) {
            cookies.setCookie('AuthToken', '', {
                expires: new Date(0)
            });
            cookies.trySignalAuthTokenChange();
            resetStore(true);
        }
    }, [
        authToken
    ]);
    useEffect(()=>{
        if (shouldLoadAppcues) {
            getProps().loadAppcues(getState().session.appcuesAccountId).then(()=>setAppcuesLoaded(true));
        }
    }, [
        shouldLoadAppcues
    ]);
    useEffect(()=>{
        const { heapIdentify, heapResetIdentity, heapAddEventProperties } = getBI();
        if (isRealUser) {
            var _getProps_visitorSyncFrame;
            const userId = userInfo.id() || '';
            const email = userInfo.email() || '';
            const userProperties = makeUserProperties(userInfo);
            Sentry.setUser({
                id: userId
            }); //For future reference - the reason we're not sending user email is because of GDPR reasons
            heapIdentify(userId, email, userProperties);
            if (userProperties.businessId != null) {
                heapAddEventProperties({
                    active_business_id: userProperties.businessId
                });
            }
            getHotjar().identify(userId, _object_spread({
                sessionId: window._sessionId
            }, userProperties));
            getProps().visitorSyncOrigin !== '' && ((_getProps_visitorSyncFrame = getProps().visitorSyncFrame) === null || _getProps_visitorSyncFrame === void 0 ? void 0 : _getProps_visitorSyncFrame.postMessage({
                type: 'anc_tracker_request',
                action: 'sync_user',
                data: {
                    userId,
                    businessId: userProperties.businessId,
                    properties: {},
                    origin: window.location.origin,
                    path: window.location.pathname
                }
            }, "https://".concat(getProps().visitorSyncOrigin)));
        } else {
            heapResetIdentity();
        }
    }, [
        userInfo,
        isRealUser
    ]);
    useEffect(()=>{
        if (appcuesLoaded && isRealUser) {
            const userId = userInfo.id() || '';
            const email = userInfo.email() || '';
            const userProperties = makeUserProperties(userInfo);
            getBI().appcuesIdentify(userId, email, userProperties);
        }
    }, [
        isRealUser,
        userInfo,
        appcuesLoaded
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: props.children
    });
}
